import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Skeleton, SkeletonGroup } from './Skeleton';

/** Surface card: seeker-900 fill, seeker-800 hairline, 8px radius. */
export function PortalCard({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-lg border border-seeker-800 bg-seeker-900',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

interface StatTileProps {
  /** 16px glyph rendered before the label. */
  icon?: ReactNode;
  label: ReactNode;
  value: ReactNode;
  /** Small trailing note beside the value, e.g. "345 this week". */
  caption?: ReactNode;
  valueClassName?: string;
  captionClassName?: string;
  isLoading?: boolean;
  className?: string;
}

/** Figma stat tile (seeker-800 fill): label row + 36px value + caption. */
export function StatTile({
  icon,
  label,
  value,
  caption,
  valueClassName,
  captionClassName,
  isLoading = false,
  className,
}: StatTileProps) {
  return (
    <div
      className={cn(
        'flex min-h-[114px] flex-col justify-center gap-3 rounded-lg bg-seeker-800 pb-4 pl-6 pr-4 pt-[18px]',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-xs leading-4 text-seeker-400 [&>svg]:size-4 [&>svg]:shrink-0">
        {icon}
        <span>{label}</span>
      </div>
      {isLoading ? (
        <SkeletonGroup className="h-10">
          <Skeleton className="h-10 w-28" />
        </SkeletonGroup>
      ) : (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span
            className={cn(
              'text-4xl font-medium leading-10 text-white tabular-nums',
              valueClassName,
            )}
          >
            {value}
          </span>
          {caption ? (
            <span
              className={cn(
                'text-xs leading-4 text-seeker-400',
                captionClassName,
              )}
            >
              {caption}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
